import { useAuthStore } from '@feature/authentication/hooks/useAuthStore'
import axios, {
  type AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { buildApiPath } from './api-routes'
import { API_BASE_URL, SSO_AUTH_MODE } from './runtime-config'
import { clearSsoCsrf, getSsoConfiguration, getSsoCsrf } from './sso'
import { getTenantHeaderValue } from './tenant'

const AUTH_BASE_PATH = buildApiPath('auth')
const AUTH_PATH_SEGMENT = `${AUTH_BASE_PATH}/`
const AUTH_REFRESH_PATH = buildApiPath('auth/refresh')
const IDEMPOTENT_METHODS = new Set(['post', 'put', 'patch'])
const IDEMPOTENCY_EXCLUDED_PATHS = [`${AUTH_BASE_PATH}/`, buildApiPath('uploads/presign')]
const PUBLIC_ROUTES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept-invitation',
  '/acesso/convite',
]
type RetriableAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

let pendingRefresh: Promise<void> | null = null

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const refreshClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

function applyRequestHeaders(config: InternalAxiosRequestConfig) {
  const headers = AxiosHeaders.from(config.headers)
  config.headers = headers
  const tenant = getTenantHeaderValue()
  if (tenant) {
    headers.set('X-Tenant', tenant)
  }

  if (shouldAttachIdempotencyKey(config) && !headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', crypto.randomUUID())
  }

  return config
}

function shouldAttachIdempotencyKey(config: InternalAxiosRequestConfig) {
  const method = config.method?.toLowerCase()
  const url = config.url ?? ''

  return Boolean(
    method &&
      IDEMPOTENT_METHODS.has(method) &&
      !IDEMPOTENCY_EXCLUDED_PATHS.some((path) => url.includes(path)),
  )
}

function isAuthRequest(url?: string) {
  return typeof url === 'string' && url.includes(AUTH_PATH_SEGMENT)
}

function getRefreshHeaders() {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const tenant = getTenantHeaderValue()
  if (tenant) {
    headers['X-Tenant'] = tenant
  }

  return headers
}

function handleUnauthorized() {
  clearSsoCsrf()
  useAuthStore.getState().logout()

  if (
    typeof window !== 'undefined' &&
    !PUBLIC_ROUTES.some((route) => window.location.pathname.startsWith(route))
  ) {
    window.location.assign('/login')
  }
}

async function refreshSession() {
  if (!pendingRefresh) {
    pendingRefresh = refreshClient
      .post(
        AUTH_REFRESH_PATH,
        {},
        {
          headers: getRefreshHeaders(),
        },
      )
      .then(() => undefined)
      .finally(() => {
        pendingRefresh = null
      })
  }

  return pendingRefresh
}

// Request interceptor - adiciona token de autenticação e tenant
apiClient.interceptors.request.use(
  async (config) => {
    applyRequestHeaders(config)
    if (SSO_AUTH_MODE) {
      if (
        new URL(config.baseURL ?? window.location.origin, window.location.origin).origin !==
        window.location.origin
      ) {
        throw new Error('SSO exige API na mesma origem do portal.')
      }
      if (!(await getSsoConfiguration()).enabled) throw new Error('SSO indisponível.')
      if (!['get', 'head', 'options'].includes(config.method?.toLowerCase() ?? 'get')) {
        const token = await getSsoCsrf()
        AxiosHeaders.from(config.headers).set(token.headerName, token.requestToken)
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor - trata erros globalmente
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (SSO_AUTH_MODE) {
        handleUnauthorized()
        return Promise.reject(error)
      }
      const originalRequest = error.config as RetriableAxiosRequestConfig | undefined

      if (originalRequest && !originalRequest._retry && !isAuthRequest(originalRequest.url)) {
        originalRequest._retry = true

        try {
          await refreshSession()
        } catch (refreshError) {
          const status = (refreshError as AxiosError).response?.status
          if (status === 400 || status === 401 || status === 403) {
            handleUnauthorized()
            // O refresh rejeitado confirma o 401 da requisição protegida original.
            return Promise.reject(error)
          }
          return Promise.reject(refreshError)
        }

        return apiClient.request(originalRequest)
      }

      handleUnauthorized()
    }
    return Promise.reject(error)
  },
)

export default apiClient
export { apiClient as apiService } // Alias para compatibilidade
